/**
 * Copy of the revenue cycle screens (ciclo de receita, ALC-5094). Spread into the `billing`
 * namespace as the top-level `revenueCycles` block. Error sentences are NOT here: they live once
 * in `REVENUE_CYCLE_ERROR_MESSAGES` (`@alinea/types`) and are rendered from the stable code.
 */
export const revenueCyclesPtBr = {
  revenueCycles: {
    title: 'Ciclos de receita',
    subtitle:
      'Quando a nota fiscal é emitida e se as linhas acumuladas viram uma fatura por período.',
    system: {
      name: 'Padrão',
      badge: 'Sistema',
      description:
        'Usado quando o contrato e o modelo não escolhem outro ciclo. A NF sai na aprovação.',
      readOnly: 'O ciclo Padrão é do sistema e não pode ser alterado.',
    },
    page: {
      listLabel: 'Ciclos de receita',
      loading: 'Carregando os ciclos',
      loadError: 'Não foi possível carregar os ciclos de receita.',
      readOnly: 'Somente leitura',
      allSaved: 'Tudo salvo',
      unsaved_one: '{{count}} alteração não salva',
      unsaved_other: '{{count}} alterações não salvas',
      discard: 'Descartar',
      save: 'Salvar alterações',
      notSaved: 'Ainda não salvo',
    },
    list: {
      createButton: 'Novo ciclo',
      aggregation: {
        none: 'Individual',
        accumulated: 'Agrega acumulados',
        reconciled: 'Agrega só conciliados',
      },
      statuses: { inactive: 'Inativo' },
      delete: 'Excluir',
      deleteConfirm: {
        title: 'Excluir ciclo?',
        description: 'O ciclo {{name}} será excluído. Esta ação não pode ser desfeita.',
        confirm: 'Excluir',
        cancel: 'Cancelar',
        success: 'Ciclo {{name}} excluído.',
      },
    },
    nfTrigger: {
      ON_APPROVAL: 'Na aprovação',
      AFTER_RECEIPT: 'Receber antes',
    },
    nfTriggerHelp: {
      ON_APPROVAL: 'A NF é emitida quando a fatura é aprovada.',
      AFTER_RECEIPT:
        'A NF é emitida depois que o recebimento é registrado. É uma escolha de quando emitir a nota, não uma verificação de que o cliente vai pagar.',
    },
    form: {
      newTitle: 'Novo ciclo de receita',
      saved: 'Ciclo {{name}} salvo.',
      unsavedChanges: {
        title: 'Sair sem salvar?',
        description: 'As alterações neste ciclo serão perdidas.',
        proceed: 'Sair sem salvar',
        stay: 'Continuar editando',
      },
      sections: {
        identity: 'Identificação',
        nf: 'Nota fiscal',
        aggregation: 'Agregação',
        preview: 'Fases',
      },
      fields: {
        name: 'Nome',
        nfTrigger: 'Quando emitir a nota fiscal',
        aggregateAccumulated: 'Agregar as linhas acumuladas em uma fatura por período',
        aggregateOnlyReconciled: 'Agregar só faturas conciliadas',
        isActive: 'Ativo',
      },
      help: {
        aggregateAccumulated:
          'Linhas acumuladas do contrato viram uma única fatura e uma única NF no fim do período. Linhas individuais seguem a nota fiscal acima.',
        aggregateOnlyReconciled:
          'A fatura do período reúne só as transações já conciliadas. Exige agregar as linhas acumuladas.',
        preview: 'Como as faturas deste ciclo andam, da aprovação à nota fiscal.',
        isActive:
          'Um ciclo inativo não pode ser escolhido em novos contratos e modelos. Quem já usa este ciclo continua com ele.',
      },
      nfIssuerHint:
        'Quem emite a NF (o Alchemy ERP ou um sistema externo) é definido em cada serviço.',
      nfIssuerLink: 'Abrir Serviços',
      aggregationExamplesLink: 'Ver exemplos',
      aggregationExamples: {
        title: 'Exemplos de agregação',
        intro:
          'Um contrato tem diárias de {{daily}}, na linha “Acumulado do período”. Em novembro foram {{days}} diárias. O dia de faturamento é 1, então o período vai de 1º a 30 de novembro.',
        cases: {
          off: {
            title: 'Agregação desligada',
            body: 'Cada diária vira uma fatura com a sua própria NF, emitida conforme a opção de nota fiscal.',
            result: 'Resultado: {{days}} faturas e {{days}} NFs de {{daily}}.',
          },
          accumulated: {
            title: 'Agregar as linhas acumuladas',
            body: 'Cada diária ainda vira uma fatura e registra a sua receita. No dia 1º de dezembro, uma fatura do período reúne as {{days}} diárias.',
            result: 'Resultado: o cliente recebe 1 NF de {{total}}.',
          },
          onlyReconciled: {
            title: 'Agregar só faturas conciliadas',
            body: 'No dia 1º de dezembro, {{reconciledDays}} diárias já foram pagas e conciliadas. A fatura do período reúne só essas {{reconciledDays}}.',
            result:
              'Resultado: 1 NF de {{reconciled}}. As {{openDays}} diárias em aberto entram na fatura do próximo dia de faturamento depois de conciliadas.',
          },
        },
        individualLines:
          'Linhas “Individual” do contrato nunca são agregadas: cada uma segue a opção de nota fiscal.',
        close: 'Fechar',
      },
      nameRequired: 'Informe o nome.',
      saving: 'Salvando…',
      inUseWarning: {
        title: 'A mudança vale para as próximas faturas',
        description:
          'Faturas já criadas mantêm o ciclo que tinham. Contratos e modelos que usam este ciclo seguem a nova configuração a partir da próxima fatura.',
      },
    },
    phases: {
      approved: 'Aprovada',
      nfIssued: 'NF emitida',
      provision: 'Provisão contábil',
      received: 'Recebida',
      periodClosed: 'Período fechado',
      reconciled: 'Faturas conciliadas',
      aggregateNf: 'NF agregada',
      externalNf: 'NF externa',
    },
    tracks: {
      standard: 'Padrão',
      receiveFirst: 'Receber antes',
      aggregate: 'Agregado',
      external: 'Faturado fora',
    },
    trackLines: {
      individual: 'Linhas individuais',
      accumulated: 'Linhas acumuladas',
    },
    phaseStates: { done: 'Concluída', current: 'Em andamento', pending: 'Pendente' },
    externalNfStates: { issuedOutside: 'Emitida fora', pending: 'Pendente' },
    picker: {
      label: 'Ciclo de receita',
      loading: 'Carregando os ciclos',
      system: 'Padrão (sistema)',
      inactive: '{{name}} (inativo)',
      suggested: 'Sugerido pelo modelo: {{name}}',
      resetToSuggested: 'Voltar ao sugerido',
      help: 'Define quando a nota fiscal é emitida e se as linhas acumuladas viram uma fatura por período.',
      contractHelp:
        'Vale para as faturas criadas depois de salvar. Faturas já criadas mantêm o ciclo que tinham.',
      templateHelp: 'O ciclo que os contratos novos deste modelo recebem. O contrato pode trocar.',
      loadError: 'Não foi possível carregar os ciclos.',
      templateLoadError:
        'Não foi possível carregar o modelo do contrato. O ciclo fica como está até o modelo carregar.',
      retry: 'Tentar novamente',
      noAccumulatedLine:
        'Nenhum serviço deste contrato é acumulado. Marque a linha na aba Serviços.',
      noAccumulatedLineTemplate:
        'Nenhum serviço deste modelo é acumulado. Marque a linha na aba Serviços.',
    },
    source: {
      CONTRACT: 'Definido no contrato',
      TEMPLATE: 'Do modelo de contrato',
      SYSTEM: 'Ciclo do sistema',
    },
    contractDialog: {
      title: 'Trocar ciclo de receita',
      description: 'Contrato {{code}}. Só as faturas criadas depois de salvar seguem o novo ciclo.',
      open: 'Trocar ciclo',
      cancel: 'Cancelar',
      save: 'Salvar ciclo',
      templateLoadError:
        'Não foi possível carregar o template do contrato. Tente de novo antes de trocar o ciclo.',
      retry: 'Tentar de novo',
    },
    nfIssuer: {
      label: 'Quem emite a nota fiscal',
      column: 'Nota fiscal',
      itemBadge: 'Nota fiscal: {{issuer}}',
      ALCHEMY: 'No Alchemy ERP',
      EXTERNAL: 'Emitida fora',
      help: {
        ALCHEMY: 'O Alchemy ERP emite a NFS-e deste serviço.',
        EXTERNAL:
          'A nota é emitida no sistema do cliente. A fatura fica com "NF externa pendente" até a nota ser vinculada.',
      },
      inUseWarning:
        'Faturas já aprovadas mantêm quem emitia a nota quando foram aprovadas. A mudança vale para as próximas aprovações.',
    },
    invoice: {
      column: 'Ciclo de receita',
      externalNfColumn: 'NF externa',
      externalNfPending: 'NF externa pendente',
      externalNfFilter: { true: 'Pendente', false: 'Sem pendência' },
      timelineTitle: 'Ciclo de receita',
      pendingBanner: {
        title: 'NF externa pendente',
        description:
          'Os serviços desta fatura emitem a nota fiscal fora do Alchemy ERP. Vincule a nota quando ela for emitida.',
        mixedDescription:
          'Parte desta fatura tem a nota emitida fora do Alchemy ERP. Vincular a nota guarda o número, a data e o ISS; nada é lançado.',
        pendingEdits: 'Salve ou descarte as alterações da fatura antes de vincular a NF externa.',
      },
      linked: 'NF externa {{number}}, emitida em {{date}}',
      linkDialog: {
        action: 'Vincular NF externa',
        title: 'Vincular NF externa',
        description:
          'Informe a nota que o sistema do cliente emitiu para esta fatura. O ISS informado acerta o ISS provisionado.',
        mixedDescription:
          'Informe a nota que o sistema do cliente emitiu para esta fatura. Nesta fatura, a nota fica só registrada; nada é lançado.',
        number: 'Número da NF',
        issueDate: 'Data de emissão',
        issAmount: 'ISS da nota',
        issAmountHelp: 'Use 0 se a nota não tem ISS.',
        required: 'Obrigatório.',
        invalidAmount: 'Informe um valor igual ou maior que zero.',
        submit: 'Vincular',
        submitting: 'Vinculando…',
        cancel: 'Cancelar',
      },
    },
  },
};

export const revenueCyclesEn: typeof revenueCyclesPtBr = {
  revenueCycles: {
    title: 'Revenue cycles',
    subtitle:
      'When the invoice’s NF is issued and whether accumulated lines become one invoice per period.',
    system: {
      name: 'Standard',
      badge: 'System',
      description:
        'Used when neither the contract nor its template picks another cycle. The NF is issued on approval.',
      readOnly: 'The Standard cycle belongs to the system and cannot be changed.',
    },
    page: {
      listLabel: 'Revenue cycles',
      loading: 'Loading the cycles',
      loadError: 'The revenue cycles could not be loaded.',
      readOnly: 'Read only',
      allSaved: 'All saved',
      unsaved_one: '{{count}} unsaved change',
      unsaved_other: '{{count}} unsaved changes',
      discard: 'Discard',
      save: 'Save changes',
      notSaved: 'Not saved yet',
    },
    list: {
      createButton: 'New cycle',
      aggregation: {
        none: 'Individual',
        accumulated: 'Aggregates accumulated lines',
        reconciled: 'Aggregates reconciled only',
      },
      statuses: { inactive: 'Inactive' },
      delete: 'Delete',
      deleteConfirm: {
        title: 'Delete cycle?',
        description: 'The cycle {{name}} will be deleted. This cannot be undone.',
        confirm: 'Delete',
        cancel: 'Cancel',
        success: 'Cycle {{name}} deleted.',
      },
    },
    nfTrigger: {
      ON_APPROVAL: 'On approval',
      AFTER_RECEIPT: 'Receive first',
    },
    nfTriggerHelp: {
      ON_APPROVAL: 'The NF is issued when the invoice is approved.',
      AFTER_RECEIPT:
        'The NF is issued after the receipt is recorded. It is a choice of when to issue the note, not a check that the customer will pay.',
    },
    form: {
      newTitle: 'New revenue cycle',
      saved: 'Cycle {{name}} saved.',
      unsavedChanges: {
        title: 'Leave without saving?',
        description: 'Your changes to this cycle will be lost.',
        proceed: 'Leave without saving',
        stay: 'Keep editing',
      },
      sections: {
        identity: 'Identification',
        nf: 'Fiscal note',
        aggregation: 'Aggregation',
        preview: 'Phases',
      },
      fields: {
        name: 'Name',
        nfTrigger: 'When to issue the fiscal note',
        aggregateAccumulated: 'Aggregate accumulated lines into one invoice per period',
        aggregateOnlyReconciled: 'Aggregate reconciled invoices only',
        isActive: 'Active',
      },
      help: {
        aggregateAccumulated:
          'The contract’s accumulated lines become one invoice and one NF at the end of the period. Individual lines follow the fiscal note setting above.',
        aggregateOnlyReconciled:
          'The period’s invoice gathers only transactions already reconciled. Requires aggregating accumulated lines.',
        preview: 'How this cycle’s invoices move, from approval to the fiscal note.',
        isActive:
          'An inactive cycle cannot be picked for new contracts and templates. Those that already use it keep it.',
      },
      nfIssuerHint: 'Who issues the NF (Alchemy ERP or an outside system) is set on each service.',
      nfIssuerLink: 'Open Services',
      aggregationExamplesLink: 'See examples',
      aggregationExamples: {
        title: 'Aggregation examples',
        intro:
          'A contract has daily fees of {{daily}}, on an “Accumulated for the period” line. November had {{days}} daily fees. The billing day is the 1st, so the period runs from November 1 to 30.',
        cases: {
          off: {
            title: 'Aggregation off',
            body: 'Each daily fee becomes an invoice with its own NF, issued as the fiscal note setting says.',
            result: 'Result: {{days}} invoices and {{days}} NFs of {{daily}}.',
          },
          accumulated: {
            title: 'Aggregate accumulated lines',
            body: 'Each daily fee still becomes an invoice and books its revenue. On December 1, one period invoice gathers the {{days}} daily fees.',
            result: 'Result: the customer gets 1 NF of {{total}}.',
          },
          onlyReconciled: {
            title: 'Aggregate reconciled invoices only',
            body: 'On December 1, {{reconciledDays}} daily fees were already paid and reconciled. The period invoice gathers only those {{reconciledDays}}.',
            result:
              'Result: 1 NF of {{reconciled}}. The {{openDays}} open daily fees join the invoice of the next billing day once reconciled.',
          },
        },
        individualLines:
          'The contract’s “Individual” lines are never aggregated: each one follows the fiscal note setting.',
        close: 'Close',
      },
      nameRequired: 'Enter a name.',
      saving: 'Saving…',
      inUseWarning: {
        title: 'The change applies to the next invoices',
        description:
          'Invoices already created keep the cycle they had. Contracts and templates that use this cycle follow the new setup from their next invoice.',
      },
    },
    phases: {
      approved: 'Approved',
      nfIssued: 'NF issued',
      provision: 'Accrual',
      received: 'Received',
      periodClosed: 'Period closed',
      reconciled: 'Invoices reconciled',
      aggregateNf: 'Aggregate NF',
      externalNf: 'External NF',
    },
    tracks: {
      standard: 'Standard',
      receiveFirst: 'Receive first',
      aggregate: 'Aggregate',
      external: 'Billed outside',
    },
    trackLines: {
      individual: 'Individual lines',
      accumulated: 'Accumulated lines',
    },
    phaseStates: { done: 'Done', current: 'In progress', pending: 'Pending' },
    externalNfStates: { issuedOutside: 'Issued outside', pending: 'Pending' },
    picker: {
      label: 'Revenue cycle',
      loading: 'Loading the cycles',
      system: 'Standard (system)',
      inactive: '{{name}} (inactive)',
      suggested: 'Suggested by the template: {{name}}',
      resetToSuggested: 'Back to suggested',
      help: 'Sets when the fiscal note is issued and whether accumulated lines become one invoice per period.',
      contractHelp:
        'Applies to invoices created after you save. Invoices already created keep the cycle they had.',
      templateHelp: 'The cycle new contracts from this template get. A contract can change it.',
      loadError: 'Could not load the cycles.',
      templateLoadError:
        'Could not load the contract template. The cycle stays as it is until the template loads.',
      retry: 'Try again',
      noAccumulatedLine:
        'No service on this contract is accumulated. Mark the line on the Services tab.',
      noAccumulatedLineTemplate:
        'No service on this template is accumulated. Mark the line on the Services tab.',
    },
    source: {
      CONTRACT: 'Set on the contract',
      TEMPLATE: 'From the contract template',
      SYSTEM: 'System cycle',
    },
    contractDialog: {
      title: 'Change revenue cycle',
      description: 'Contract {{code}}. Only invoices created after you save follow the new cycle.',
      open: 'Change cycle',
      cancel: 'Cancel',
      save: 'Save cycle',
      templateLoadError:
        'Could not load the contract template. Try again before you change the cycle.',
      retry: 'Try again',
    },
    nfIssuer: {
      label: 'Who issues the fiscal note',
      column: 'Fiscal note',
      itemBadge: 'Fiscal note: {{issuer}}',
      ALCHEMY: 'In Alchemy ERP',
      EXTERNAL: 'Issued outside',
      help: {
        ALCHEMY: 'Alchemy ERP issues this service’s NFS-e.',
        EXTERNAL:
          'The note is issued in the customer’s own system. The invoice shows "External NF pending" until the note is linked.',
      },
      inUseWarning:
        'Invoices already approved keep who issued their note when they were approved. The change applies to the next approvals.',
    },
    invoice: {
      column: 'Revenue cycle',
      externalNfColumn: 'External NF',
      externalNfPending: 'External NF pending',
      externalNfFilter: { true: 'Pending', false: 'Not pending' },
      timelineTitle: 'Revenue cycle',
      pendingBanner: {
        title: 'External NF pending',
        description:
          'This invoice’s services issue their fiscal note outside Alchemy ERP. Link the note once it is issued.',
        mixedDescription:
          'Part of this invoice has its note issued outside Alchemy ERP. Linking the note stores its number, date and ISS; nothing is posted.',
        pendingEdits: 'Save or discard the invoice changes before you link the external NF.',
      },
      linked: 'External NF {{number}}, issued on {{date}}',
      linkDialog: {
        action: 'Link external NF',
        title: 'Link external NF',
        description:
          'Enter the note the customer’s system issued for this invoice. Its ISS settles the accrued ISS.',
        mixedDescription:
          'Enter the note the customer’s system issued for this invoice. On this invoice the note is only recorded; nothing is posted.',
        number: 'NF number',
        issueDate: 'Issue date',
        issAmount: 'ISS on the note',
        issAmountHelp: 'Use 0 if the note has no ISS.',
        required: 'Required.',
        invalidAmount: 'Enter an amount of zero or more.',
        submit: 'Link',
        submitting: 'Linking…',
        cancel: 'Cancel',
      },
    },
  },
};
